import { useState } from "react";
import moment from "moment";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Snackbar,
  TextField,
} from "@mui/material";
import {
  CalendarMonth,
  ContentCopy,
  IosShare,
  Notes,
} from "@mui/icons-material";
import { QRCodeSVG } from "qrcode.react";
import { CreateParamsJson } from "../../helper/pathHelper";
import { buildShortLink } from "../../helper/aiPrompt";
import { appBase, copyText } from "../../helper/clipboard";
import { buildIcs, buildMarkdown, downloadFile } from "../../helper/share";
import { useLanguage } from "../../i18n/LanguageContext";

const VIEWS = [
  { key: "", label: "share.viewBoard" },
  { key: "fullscreen", label: "share.viewFullscreen" },
];

// Shares the plan on the board: a QR code and link (start time in UTC, so
// it runs at the same moment for everyone), the plan as text for an
// invitation, and a calendar entry for the whole session.
export default function SharePlan() {
  const json = CreateParamsJson();
  const [open, setOpen] = useState(false);
  const [view, setView] = useState("fullscreen");
  const [message, setMessage] = useState(null);
  const { language, t } = useLanguage();

  const agenda = json.agenda ? JSON.parse(json.agenda) : [];
  if (!json.starttime || agenda.length === 0) {
    return null;
  }
  const starttime = moment(json.starttime);
  const link = buildShortLink(starttime, agenda, appBase(), {
    absolute: true,
    page: view,
  });

  const copy = async (text, done) => {
    if (await copyText(text)) {
      setMessage(done);
    }
  };

  return (
    <>
      <Button
        size="small"
        variant="outlined"
        startIcon={<IosShare />}
        onClick={() => setOpen(true)}
      >
        {t("share.button")}
      </Button>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="xs"
        slotProps={{ paper: { className: "card-details-dialog" } }}
      >
        <DialogTitle>{t("share.title")}</DialogTitle>
        <DialogContent className="share-plan">
          <div
            className="example-variants"
            role="group"
            aria-label={t("share.linkOpens")}
          >
            {VIEWS.map((option) => (
              <button
                key={option.key}
                type="button"
                className={`example-chip${
                  option.key === view ? " is-selected" : ""
                }`}
                aria-pressed={option.key === view}
                onClick={() => setView(option.key)}
              >
                {t(option.label)}
              </button>
            ))}
          </div>
          <p className="share-hint">
            {view
              ? t("share.hintFullscreen")
              : t("share.hintBoard")}
          </p>
          <div className="share-qr">
            <QRCodeSVG
              value={link}
              size={208}
              level="L"
              bgColor="#fffbf2"
              fgColor="#2e2a22"
              title={t("share.qrTitle")}
            />
          </div>
          <TextField
            variant="standard"
            label={t("share.link")}
            value={link}
            fullWidth
            slotProps={{
              htmlInput: { readOnly: true, onFocus: (e) => e.target.select() },
            }}
          />
          <div className="share-actions">
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopy />}
              onClick={() => copy(link, t("share.linkCopied"))}
            >
              {t("share.copyLink")}
            </Button>
            {navigator.share && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<IosShare />}
                onClick={() =>
                  navigator
                    .share({ title: t("exports.agenda"), url: link })
                    .catch(() => {})
                }
              >
                {t("share.shareNative")}
              </Button>
            )}
            <Button
              size="small"
              variant="outlined"
              startIcon={<Notes />}
              onClick={() =>
                copy(
                  buildMarkdown(starttime, agenda, link, language),
                  t("share.textCopied")
                )
              }
            >
              {t("share.copyText")}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<CalendarMonth />}
              onClick={() =>
                downloadFile(
                  `${t("share.fileName")}-${starttime.format(
                    "YYYY-MM-DD-HHmm"
                  )}.ics`,
                  buildIcs(starttime, agenda, link, { language }),
                  "text/calendar;charset=utf-8"
                )
              }
            >
              {t("share.calendar")}
            </Button>
          </div>
        </DialogContent>
        <DialogActions>
          <Button color="inherit" onClick={() => setOpen(false)}>
            {t("common.close")}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={2500}
        onClose={() => setMessage(null)}
        message={message}
      />
    </>
  );
}
